import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { colors, Card, Button, LoadingSpinner, EmptyState } from '../../shared';
import { ArrowLeft, Clock, GitBranch, Play, Check, Archive, Eye } from 'lucide-react';

const VersionCard = ({ version, isLive, onDeploy, onCompare }) => {
  const statusColors = {
    live: colors.green,
    testing: colors.orange,
    archived: colors.textMuted,
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card className="mb-3" style={{ border: `1px solid ${isLive ? colors.green : colors.border}` }}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center font-bold"
            style={{ background: colors.yellowDim, color: colors.yellow }}
          >
            v{version.version}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium" style={{ color: colors.textPrimary }}>
                Version {version.version}
              </span>
              <span
                className="px-2 py-0.5 rounded text-xs font-medium"
                style={{ background: `${statusColors[version.status]}20`, color: statusColors[version.status] }}
              >
                {version.status}
              </span>
            </div>
            <p className="text-xs" style={{ color: colors.textMuted }}>
              {formatDate(version.createdAt)} by {version.createdBy}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onCompare && (
            <button
              onClick={() => onCompare(version)}
              className="p-2 rounded-lg"
              style={{ background: colors.cardBg, color: colors.textMuted }}
              title="Compare"
            >
              <Eye size={16} />
            </button>
          )}
          {onDeploy && version.status !== 'live' && (
            <Button
              variant="success"
              onClick={() => onDeploy(version)}
              className="text-xs px-3 py-1.5"
            >
              <Play size={12} />
              Deploy
            </Button>
          )}
        </div>
      </div>

      {version.changeNotes && (
        <p className="text-sm mb-3 pl-13" style={{ color: colors.textSecondary }}>
          {version.changeNotes}
        </p>
      )}

      <div className="flex items-center gap-6 text-xs pl-13" style={{ color: colors.textMuted }}>
        <span>{version.performanceSnapshot?.totalCalls || 0} calls</span>
        <span>{((version.performanceSnapshot?.successRate || 0) * 100).toFixed(0)}% success</span>
        <span>{version.model}</span>
      </div>
    </Card>
  );
};

const DiffView = ({ oldVersion, newVersion, onClose }) => {
  if (!oldVersion || !newVersion) return null;

  const highlightDiff = (oldText, newText) => {
    // Simple line-by-line diff
    const oldLines = (oldText || '').split('\n');
    const newLines = (newText || '').split('\n');
    const maxLines = Math.max(oldLines.length, newLines.length);

    const result = [];
    for (let i = 0; i < maxLines; i++) {
      const oldLine = oldLines[i] || '';
      const newLine = newLines[i] || '';

      if (oldLine !== newLine) {
        if (oldLine) result.push({ type: 'removed', text: oldLine });
        if (newLine) result.push({ type: 'added', text: newLine });
      } else {
        result.push({ type: 'unchanged', text: oldLine });
      }
    }

    return result;
  };

  const systemDiff = highlightDiff(oldVersion.systemPrompt, newVersion.systemPrompt);
  const userDiff = highlightDiff(oldVersion.userPromptTemplate, newVersion.userPromptTemplate);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)' }}>
      <Card className="w-full max-w-5xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>
            Compare v{oldVersion.version} → v{newVersion.version}
          </h2>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              System Prompt Changes
            </h3>
            <div className="p-4 rounded-lg font-mono text-sm overflow-auto" style={{ background: colors.inputBg }}>
              {systemDiff.map((line, idx) => (
                <div
                  key={idx}
                  className="py-0.5"
                  style={{
                    color: line.type === 'added' ? colors.green :
                           line.type === 'removed' ? colors.red : colors.textSecondary,
                    background: line.type === 'added' ? `${colors.green}10` :
                                line.type === 'removed' ? `${colors.red}10` : 'transparent',
                  }}
                >
                  {line.type === 'added' && '+ '}
                  {line.type === 'removed' && '- '}
                  {line.text || ' '}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              User Prompt Template Changes
            </h3>
            <div className="p-4 rounded-lg font-mono text-sm overflow-auto" style={{ background: colors.inputBg }}>
              {userDiff.map((line, idx) => (
                <div
                  key={idx}
                  className="py-0.5"
                  style={{
                    color: line.type === 'added' ? colors.green :
                           line.type === 'removed' ? colors.red : colors.textSecondary,
                    background: line.type === 'added' ? `${colors.green}10` :
                                line.type === 'removed' ? `${colors.red}10` : 'transparent',
                  }}
                >
                  {line.type === 'added' && '+ '}
                  {line.type === 'removed' && '- '}
                  {line.text || ' '}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Model Changes
              </h3>
              <div className="p-3 rounded-lg" style={{ background: colors.inputBg }}>
                <p style={{ color: oldVersion.model !== newVersion.model ? colors.orange : colors.textMuted }}>
                  {oldVersion.model} → {newVersion.model}
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Parameter Changes
              </h3>
              <div className="p-3 rounded-lg text-sm" style={{ background: colors.inputBg }}>
                <p style={{ color: oldVersion.maxTokens !== newVersion.maxTokens ? colors.orange : colors.textMuted }}>
                  Max Tokens: {oldVersion.maxTokens} → {newVersion.maxTokens}
                </p>
                <p style={{ color: oldVersion.temperature !== newVersion.temperature ? colors.orange : colors.textMuted }}>
                  Temperature: {oldVersion.temperature} → {newVersion.temperature}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default function PromptVersionHistory({ prompt, onBack, onDeploy }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareVersions, setCompareVersions] = useState(null);

  useEffect(() => {
    loadVersions();
  }, [prompt.id]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'ai_prompt_versions'),
          where('promptId', '==', prompt.id)
        )
      );

      const versionsData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => b.version - a.version);

      setVersions(versionsData);
    } catch (error) {
      console.error('Error loading versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = (version) => {
    const currentVersionData = versions.find(v => v.status === 'live') || versions[0];
    if (currentVersionData && currentVersionData.version !== version.version) {
      setCompareVersions({ old: version, new: currentVersionData });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-lg"
          style={{ background: colors.cardBg, color: colors.textMuted }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>
            Version History
          </h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            {prompt.name} ({prompt.id})
          </p>
        </div>
      </div>

      {/* Timeline */}
      {versions.length === 0 ? (
        <EmptyState
          icon={GitBranch}
          title="No versions yet"
          description="Version history will appear here when you create new versions"
        />
      ) : (
        <div className="relative">
          {/* Vertical line */}
          <div
            className="absolute left-5 top-0 bottom-0 w-0.5"
            style={{ background: colors.border }}
          />

          <div className="space-y-0">
            {versions.map((version, idx) => (
              <VersionCard
                key={version.id}
                version={version}
                isLive={version.status === 'live'}
                onDeploy={onDeploy}
                onCompare={idx < versions.length - 1 ? handleCompare : null}
              />
            ))}
          </div>
        </div>
      )}

      {/* Diff View Modal */}
      {compareVersions && (
        <DiffView
          oldVersion={compareVersions.old}
          newVersion={compareVersions.new}
          onClose={() => setCompareVersions(null)}
        />
      )}
    </div>
  );
}
